import React, { useEffect, useRef, useState } from "react";
import { useMutation, useQuery, gql } from "@apollo/client";
import { Loader, Modal, Button as MButton } from "semantic-ui-react";
import RegisterToApplyButton from "./RegisterToApplyButton";
import Button from "./Button";
import appText from "@/lang/appText";
import { useRouter } from "next/router";
import {
  CHECK_USER_APPLICATION_STATUS_QUERY,
  ME_USER_QUERY,
} from "@/graphql/queries/users";

const APPLY_TO_JOB_MUTATION = gql`
  mutation APPLY_TO_JOB_MUTATION($jobId: ID!) {
    createApplication(job: $jobId) {
      id
      job {
        id
        title
      }
    }
  }
`;

const ApplyToJobButton = (props) => {
  const [openModal, setOpenModal] = useState(false);
  const shouldAutoOpenRef = useRef(false);
  const router = useRouter();
  const { q, location, category } = router.query;

  const isMedical =
    (q && q.toLowerCase().includes("medical")) ||
    (category && category.toLowerCase().includes("medical"));

  const { error, data } = useQuery(ME_USER_QUERY);
  const me = data?.me;
  const shouldCheckApply =
    !!me && me.role?.name === "candidate" && me.resumes?.length > 0;

  const { data: applyStatusData } = useQuery(
    CHECK_USER_APPLICATION_STATUS_QUERY,
    {
      variables: { jobId: props.jobId },
      skip: !shouldCheckApply,
    }
  );

  const closeApplyModal = () => {
    shouldAutoOpenRef.current = false;
    setOpenModal(false);
  };

  const [applyToJobMutation, applyState] = useMutation(APPLY_TO_JOB_MUTATION, {
    variables: { jobId: props.jobId },
    refetchQueries: shouldCheckApply
      ? [
          {
            query: CHECK_USER_APPLICATION_STATUS_QUERY,
            variables: { jobId: props.jobId },
          },
        ]
      : [],
    update(cache, { data: mutationData }) {
      const created = mutationData?.createApplication;
      if (!created?.job?.id) return;
      try {
        const existing = cache.readQuery({
          query: CHECK_USER_APPLICATION_STATUS_QUERY,
          variables: { jobId: props.jobId },
        });
        if (!existing?.me) return;
        const already = (existing.me.applications || []).some(
          (application) => application.job?.id === created.job.id
        );
        cache.writeQuery({
          query: CHECK_USER_APPLICATION_STATUS_QUERY,
          variables: { jobId: props.jobId },
          data: {
            me: {
              ...existing.me,
              applications: already
                ? existing.me.applications
                : [
                    ...(existing.me.applications || []),
                    {
                      __typename: "Application",
                      id: created.id,
                      job: { __typename: "Job", id: created.job.id },
                    },
                  ],
            },
          },
        });
      } catch (err) {
        return;
      }
    },
  });

  const userApplied =
    (applyStatusData?.me?.applications || []).some(
      (application) => application.job?.id === props.jobId
    ) || !!applyState.data?.createApplication?.job;

  useEffect(() => {
    if (shouldAutoOpenRef.current && me?.resumes?.length > 0 && !userApplied) {
      shouldAutoOpenRef.current = false;
      setOpenModal(true);
    }
  }, [me, userApplied]);

  if (error) {
    console.log(error);
    return <p>Something went wrong</p>;
  }
  if (me && me.role.name !== "candidate") return null;

  if (!me || me.resumes.length === 0) {
    return (
      <RegisterToApplyButton
        jobId={props.jobId}
        compact={props.compact}
        me={me}
        onApplyFlowStarted={() => {
          shouldAutoOpenRef.current = true;
        }}
      />
    );
  }

  if (applyState.loading) {
    return (
      <Loader active inline="centered">
        Applying...
      </Loader>
    );
  }
  if (applyState.error) {
    return <p>Something Failed, please try again later.</p>;
  }

  return (
    <React.Fragment>
      <Modal
        trigger={
          <Button
            fullWidth={!props.compact}
            disabled={userApplied || applyState.loading}
            loading={applyState.loading}
            color={isMedical ? "5" : ""}
            styles={
              props.compact
                ? `height: 40px; font-size: 0.95em; min-width: 148px; border-radius: 8px; float: none; box-shadow: none;`
                : ""
            }
          >
            {userApplied
              ? `${appText.messages.application.applied} 😊`
              : `${appText.messages.application.applyNow} ${
                  isMedical ? "🧑🏻‍⚕️" : "😀"
                }`}
          </Button>
        }
        open={openModal}
        onClose={closeApplyModal}
        onOpen={() => setOpenModal(true)}
      >
        <Modal.Header>Confirmation!</Modal.Header>
        <Modal.Content>
          <p>
            Are you sure you want to apply to this job in{" "}
            <b>{props.location}?</b>
          </p>
        </Modal.Content>
        <Modal.Actions>
          <MButton
            color="green"
            onClick={() => {
              closeApplyModal();
              applyToJobMutation();
            }}
          >
            Yes
          </MButton>
          <MButton color="red" onClick={closeApplyModal}>
            No
          </MButton>
        </Modal.Actions>
      </Modal>
    </React.Fragment>
  );
};

export default ApplyToJobButton;
